import { useFlag, useNewCode } from '@calendly/feature-check-react'
import { useQueryParams } from '@calendly/hooks'
import { createContext, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'

import { useMeetingFromCache, useSSEConnectedForPolling } from 'components/meetings'
import { useActiveMeeting, useLiveMeetingDetailPolling } from 'components/meetings/hooks'
import { defensiveMergeMeeting } from 'components/meetings/hooks/helpers'
import { isPendingEvent } from 'components/meetings/meeting_detail/utils'
import { useMeetingsRealtimeFeaturesEnabled } from 'features/meetings/hooks'
import { useRightRail } from 'features/right_rail'
import { useScope } from 'hooks/meetings/use_scope'
import { timezoneSelector } from 'selectors/current_user'
import { useLazyGQLGetMeetingQuery } from 'shared/api/meetings'

import { useGetMeetingQuery, useLazyGetMeetingQuery } from '../api'
import { useGetMeetingQuery as useGQLGetMeetingQuery, useLazyGetMeetingQuery as useLazyGetMeetingFromSearchQuery } from '../graphql_api'
import { useSelectedView } from '../hooks/use_selected_view'
import { mapNodeToMeeting } from '../utils'

export const MeetingDetailContext = createContext({})

/**
 * Provider for Meetings detail. Aligned with realtime meetings: runs REST getMeeting
 * in the provider and merges cache + REST so full details (e.g. location for INBOUND_CALL)
 * are available. Passes merged data as preloadedMeetingData to useActiveMeeting.
 */
export const MeetingDetailProvider = ({ eventId, shareId, children }) => {
  const showNonOrganizerMeetings = useFlag('show_non_organizer_meetings', false)
  const showNotetakerIneligibleExternalEvents = true
  const selectedView = useSelectedView()
  const { scopeType, scopeId } = useScope()
  const timezone = useSelector(timezoneSelector)
  const { preload: preloadParam } = useQueryParams()
  const isRealtimeFeatureEnabled = useNewCode('meetings_recaps_realtime_feature', false)?.enabled
  const allowPreload = Boolean(isRealtimeFeatureEnabled && (preloadParam === 'true' || preloadParam === true))
  const hasRealtimeFeaturesEnabled = useMeetingsRealtimeFeaturesEnabled()
  const isSSEConnectedFromHook = useSSEConnectedForPolling()
  // On realtime features disabled, ignore global SSE state so detail polling stays disabled without leaking realtime state.
  const isSSEConnected = hasRealtimeFeaturesEnabled ? isSSEConnectedFromHook : false

  const [triggerGraphQLFallback, {
    data: graphqlFallbackData,
    isLoading: graphqlFallbackLoading,
    isUninitialized: graphqlFallbackUninitialized,
  }] = useLazyGQLGetMeetingQuery()

  // Get meeting from RTK Query cache for instant display when opening from list
  const shouldUseCache = eventId && !shareId
  const cacheMeetingData = useMeetingFromCache(
    shouldUseCache ? eventId : null,
    mapNodeToMeeting,
    {
      scopeType,
      scopeId,
      isOrganizer: !showNonOrganizerMeetings,
      includeNotetakerIneligibleExternalEvents: showNotetakerIneligibleExternalEvents,
    }
  )

  // Skip REST getMeeting for non-Calendly (external) events; scheduled_events API is for Calendly events only
  const isCalendlyMeeting = useMemo(() => {
    if (!cacheMeetingData) return true // No cache yet: allow REST call to determine
    return !cacheMeetingData.kind || cacheMeetingData.kind === 'CALENDLY'
  }, [cacheMeetingData])

  // External (non-Calendly) from cache only — used for polling decisions. REST is skipped for external.
  const isExternalEventFromCache = useMemo(
    () => Boolean(cacheMeetingData?.kind && cacheMeetingData.kind !== 'CALENDLY'),
    [cacheMeetingData]
  )

  // GraphQL fallback meeting (lazy) — used to derive external status for deep-linked external meetings.
  const graphqlFallbackMeeting = useMemo(() => {
    if (!graphqlFallbackData?.uuid || graphqlFallbackData.uuid !== eventId) return null
    return mapNodeToMeeting(graphqlFallbackData, timezone || 'UTC')
  }, [graphqlFallbackData, eventId, timezone])

  // External for polling: cache OR GraphQL fallback. Ensures 5s polling starts after fallback loads for deep-linked external meetings.
  const isExternalEventForPolling = useMemo(
    () => isExternalEventFromCache || Boolean(graphqlFallbackMeeting?.kind && graphqlFallbackMeeting.kind !== 'CALENDLY'),
    [isExternalEventFromCache, graphqlFallbackMeeting]
  )

  const {
    detailPollingConfig,
    shouldPollGraphQLForExternal,
    graphQLPollingQueryArgs,
  } = useLiveMeetingDetailPolling({
    enabled: hasRealtimeFeaturesEnabled,
    isSSEConnected, // false when realtime features disabled
    eventId,
    shareId,
    isExternalEvent: isExternalEventForPolling,
    selectedView,
    isOrganizer: !showNonOrganizerMeetings,
    includeNotetakerIneligibleExternalEvents: showNotetakerIneligibleExternalEvents,
  })

  // REST getMeeting in provider (same pattern as realtime) so we get full details e.g. phone location
  const shouldFetchRestApi = eventId && !shareId && selectedView !== 'pending' && scopeType && isCalendlyMeeting
  const restApiQueryArgs = useMemo(() => {
    if (!shouldFetchRestApi || !scopeType || !isCalendlyMeeting) {
      return { eventId: undefined, selectedView: undefined, scopeType: undefined, scopeId: undefined }
    }
    return { eventId, selectedView, scopeType, scopeId }
  }, [shouldFetchRestApi, eventId, scopeType, scopeId, selectedView, isCalendlyMeeting])

  const {
    data: restApiMeetingData,
    isError: restApiError,
    isSuccess: restApiSuccess,
    error: restApiErrorPayload,
  } = useGetMeetingQuery(restApiQueryArgs, {
    skip: !shouldFetchRestApi || !scopeType || !isCalendlyMeeting,
    refetchOnMountOrArgChange: true,
    ...detailPollingConfig,
  })

  // Include graphqlFallbackMeeting so deep-linked external meetings (no cache/REST) get isExternalEvent true
  // and the merge logic below incorporates graphQLPolledMeeting into mergedMeetingData.
  const isExternalEvent = useMemo(() => {
    const meeting = cacheMeetingData || restApiMeetingData || graphqlFallbackMeeting
    return meeting && meeting.kind && meeting.kind !== 'CALENDLY'
  }, [cacheMeetingData, restApiMeetingData, graphqlFallbackMeeting])

  const { data: graphQLPollingData } = useGQLGetMeetingQuery(graphQLPollingQueryArgs, {
    ...detailPollingConfig,
    skip: !shouldPollGraphQLForExternal || !Object.keys(graphQLPollingQueryArgs).length,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
  })

  const graphQLPolledMeeting = useMemo(() => {
    if (!graphQLPollingData?.uuid || graphQLPollingData.uuid !== eventId) return null
    return mapNodeToMeeting(graphQLPollingData, timezone || 'UTC')
  }, [graphQLPollingData, eventId, timezone])

  // Merge cache + REST + GraphQL polling (for external) + GraphQL fallback so preloaded has full details
  const mergedMeetingData = useMemo(() => {
    if (isExternalEvent && graphQLPolledMeeting) {
      const base = defensiveMergeMeeting(
        restApiMeetingData || cacheMeetingData,
        graphQLPolledMeeting,
        { preserveMeetingIntelligence: false }
      )
      return defensiveMergeMeeting(base, graphqlFallbackMeeting, { preserveMeetingIntelligence: false })
    }
    const cachePlusRest = defensiveMergeMeeting(cacheMeetingData, restApiMeetingData, { preserveMeetingIntelligence: false })
    return defensiveMergeMeeting(cachePlusRest, graphqlFallbackMeeting, { preserveMeetingIntelligence: false })
  }, [
    isExternalEvent,
    graphQLPolledMeeting,
    cacheMeetingData,
    restApiMeetingData,
    graphqlFallbackMeeting,
  ])

  const preloadedDataForActiveMeeting = useMemo(() => {
    if (mergedMeetingData && mergedMeetingData.uuid === eventId) return mergedMeetingData
    if (cacheMeetingData && cacheMeetingData.uuid === eventId) return cacheMeetingData
    if (graphQLPolledMeeting && graphQLPolledMeeting.uuid === eventId) return graphQLPolledMeeting
    if (graphqlFallbackMeeting && graphqlFallbackMeeting.uuid === eventId) return graphqlFallbackMeeting
    return null
  }, [mergedMeetingData, cacheMeetingData, graphQLPolledMeeting, graphqlFallbackMeeting, eventId])

  const { activeMeeting, clearActiveMeeting, updateActiveMeeting, isLoading } = useActiveMeeting({
    selectedView,
    scopeType,
    scopeId,
    eventId,
    shareId,
    isOrganizer: !showNonOrganizerMeetings,
    includeNotetakerIneligibleExternalEvents: showNotetakerIneligibleExternalEvents,
    preloadedMeetingData: preloadedDataForActiveMeeting,
    // Avoid duplicate getMeeting: when we have eventId (no shareId), provider fetches once scope is ready;
    // don't trigger lazy fetch in useActiveMeeting so we don't get duplicate/pending calls on refresh (scope may be delayed).
    skipLazyFetchWhenProviderFetches: Boolean(eventId && !shareId),
    // Dependency injection for the consolidated hook
    useLazyGetMeetingQuery,
    useLazyGetMeetingFromSearchQuery,
    isPendingEvent,
    mapNodeToMeeting,
  })

  const { close } = useRightRail()
  const handled404ForEventRef = useRef(null)
  const clearedAfterGraphQLFallbackRef = useRef(null)

  // Reset 404/clear refs when eventId changes so each meeting gets a fresh 404 flow
  useEffect(() => {
    handled404ForEventRef.current = null
    clearedAfterGraphQLFallbackRef.current = null
  }, [eventId])

  // Detect REST 404: either status code or root-api "missing" shape ({ type: "missing", payload: { message: "not found" } })
  const isRest404 = useMemo(() => {
    if (!restApiError || !restApiErrorPayload) return false
    const s = restApiErrorPayload.status
      ?? restApiErrorPayload.originalStatus
      ?? restApiErrorPayload.data?.status
      ?? restApiErrorPayload.data?.originalStatus
    if (s != null && Number(s) === 404) return true
    if (restApiErrorPayload?.type === 'missing') return true
    return false
  }, [restApiError, restApiErrorPayload])

  const isFullDataReady = useMemo(() => {
    if (allowPreload) return true
    if (!shouldFetchRestApi) return !!activeMeeting
    const restSuccessWithData = restApiSuccess && restApiMeetingData?.uuid === eventId
    const rest404AndGraphQLDone = isRest404 && !graphqlFallbackUninitialized && !graphqlFallbackLoading
    return restSuccessWithData || rest404AndGraphQLDone
  }, [
    allowPreload,
    shouldFetchRestApi,
    activeMeeting,
    restApiSuccess,
    restApiMeetingData,
    eventId,
    isRest404,
    graphqlFallbackUninitialized,
    graphqlFallbackLoading,
  ])

  // When REST returns 404 or succeeds with no data, trigger GraphQL fallback.
  // GraphQL can return meetings (incl. external events) that REST may not have due to period/scope.
  // Without this, meetings not in the list (e.g. deep link from past/future) stay stuck in loading.
  useEffect(() => {
    if (!shouldFetchRestApi || !eventId) return
    if (handled404ForEventRef.current === eventId) return

    const rest404 = isRest404
    const restSuccessNoData = restApiSuccess && !restApiMeetingData?.uuid && !cacheMeetingData?.uuid
    if (!rest404 && !restSuccessNoData) return

    handled404ForEventRef.current = eventId
    triggerGraphQLFallback({
      uuid: eventId,
      isOrganizer: !showNonOrganizerMeetings,
      includeNotetakerIneligibleExternalEvents: showNotetakerIneligibleExternalEvents,
    })
  }, [
    shouldFetchRestApi,
    isRest404,
    restApiSuccess,
    restApiMeetingData,
    cacheMeetingData,
    eventId,
    showNonOrganizerMeetings,
    showNotetakerIneligibleExternalEvents,
    triggerGraphQLFallback,
  ])

  // When GraphQL fallback has resolved and we have no meeting data, clear selection and close rail.
  // Only run after we've triggered the fallback (graphqlFallbackUninitialized false) and loading has finished.
  // Never clear when we have meeting data (REST or cache succeeded) so valid meetings don't close.
  useEffect(() => {
    if (!eventId || handled404ForEventRef.current !== eventId) return
    if (graphqlFallbackUninitialized || graphqlFallbackLoading) return
    if (graphqlFallbackData?.uuid === eventId) return
    if (preloadedDataForActiveMeeting?.uuid === eventId) return
    if (clearedAfterGraphQLFallbackRef.current === eventId) return
    clearedAfterGraphQLFallbackRef.current = eventId
    clearActiveMeeting()
    close()
  }, [
    eventId,
    graphqlFallbackUninitialized,
    graphqlFallbackLoading,
    graphqlFallbackData,
    preloadedDataForActiveMeeting,
    clearActiveMeeting,
    close,
  ])

  const context = useMemo(() => {
    const handleClose = () => {
      clearActiveMeeting() // clears URL params and selected meeting state
      close()
    }

    const showFullLoader = !allowPreload && !isFullDataReady
    return {
      selectedEvent: showFullLoader ? null : activeMeeting,
      handleClose,
      updateActiveMeeting,
      isLoading: showFullLoader ? true : isLoading,
    }
  }, [activeMeeting, clearActiveMeeting, updateActiveMeeting, isLoading, close, allowPreload, isFullDataReady])

  return (
    <MeetingDetailContext.Provider value={context}>
      {children}
    </MeetingDetailContext.Provider>
  )
}
